import { useWindowEvent } from "@/hooks"
import React, { Children, ReactNode, useCallback, useLayoutEffect, useRef, useState, useMemo } from "react"

interface MasonryProps {
  breakpointCols?: number | { default: number; [key: number]: number }
  className?: string
  columnClassName?: string
  children?: ReactNode
  columnAttrs?: React.HTMLAttributes<HTMLDivElement>
  column?: React.HTMLAttributes<HTMLDivElement>
  [key: string]: any
}

interface ColumnAttributes extends React.HTMLAttributes<HTMLDivElement> {
  style: {
    width: string
    [key: string]: any
  }
  className?: string
}

const DEFAULT_COLUMNS = 2

const reCalculateColumnCount = (breakpointCols?: number | { default: number; [key: number]: number }) => {
  const windowWidth = (window && window.innerWidth) || Infinity
  let breakpointColsObject = breakpointCols

  // Allow passing a single number to `breakpointCols` instead of an object
  if (typeof breakpointColsObject !== "object") {
    breakpointColsObject = {
      default: breakpointColsObject || DEFAULT_COLUMNS,
    }
  }

  let matchedBreakpoint = Infinity
  let columns = breakpointColsObject.default || DEFAULT_COLUMNS

  for (let breakpoint in breakpointColsObject) {
    const optBreakpoint = parseInt(breakpoint)
    const isCurrentBreakpoint = optBreakpoint > 0 && windowWidth <= optBreakpoint

    if (isCurrentBreakpoint && optBreakpoint < matchedBreakpoint) {
      matchedBreakpoint = optBreakpoint
      columns = breakpointColsObject[breakpoint]
    }
  }

  columns = Math.max(1, columns || 1)
  return columns
}

const reCalculateColumnCountDebounce = (
  columnCountCallback: () => void,
  lastFrameRef: React.MutableRefObject<number>,
) => {
  if (!window || !window.requestAnimationFrame) {
    // IE10+
    columnCountCallback()
  }

  if (window.cancelAnimationFrame) {
    // IE10+
    const lastFrame = lastFrameRef.current
    window.cancelAnimationFrame(lastFrame)
  }

  lastFrameRef.current = window.requestAnimationFrame(columnCountCallback)
}

const itemsInColumns = (currentColumnCount: number, children: ReactNode) => {
  const itemsInColumns = new Array(currentColumnCount)

  // Force children to be handled as an array
  const items = Children.toArray(children)

  for (let i = 0; i < items.length; i++) {
    const columnIndex = i % currentColumnCount

    if (!itemsInColumns[columnIndex]) {
      itemsInColumns[columnIndex] = []
    }

    itemsInColumns[columnIndex].push(items[i])
  }

  return itemsInColumns
}

const logDeprecated = (message: string) => {
  console.error("[Masonry]", message)
}

const renderColumns = (
  children: ReactNode,
  currentColumnCount: number,
  column: React.HTMLAttributes<HTMLDivElement> = {},
  columnAttrs?: React.HTMLAttributes<HTMLDivElement>,
  columnClassName?: string,
) => {
  const childrenInColumns = itemsInColumns(currentColumnCount, children)
  const columnWidth = `${100 / childrenInColumns.length}%`
  let className = columnClassName

  if (className && typeof className !== "string") {
    logDeprecated('The property "columnClassName" requires a string')

    // This is a deprecated default and will be removed soon.
    if (typeof className === "undefined") {
      className = "my-masonry-grid_column"
    }
  }

  const columnAttributes = {
    // NOTE: the column property is undocumented and considered deprecated.
    // It is an alias of the `columnAttrs` property
    ...column,
    ...columnAttrs,
    style: {
      ...columnAttrs?.style,
      width: columnWidth,
    },
    className,
  }

  return childrenInColumns.map((items, i) => {
    return (
      <div {...columnAttributes} key={i}>
        {items}
      </div>
    )
  })
}

const Masonry: React.FC<MasonryProps> = ({
  breakpointCols,
  className,
  columnClassName,
  children,
  columnAttrs,
  column,
  ...rest
}) => {
  const [columnCount, setColumnCount] = useState(() => {
    if (breakpointCols && typeof breakpointCols === "object") {
      return breakpointCols.default || DEFAULT_COLUMNS
    }
    return typeof breakpointCols === "number" ? breakpointCols : DEFAULT_COLUMNS
  })

  const lastFrameRef = useRef(0)

  // Memoize itemsInColumns calculation
  const childrenInColumns = useMemo(() => {
    const itemsInColumns = new Array(columnCount)
    const items = Children.toArray(children)

    for (let i = 0; i < items.length; i++) {
      const columnIndex = i % columnCount
      if (!itemsInColumns[columnIndex]) {
        itemsInColumns[columnIndex] = []
      }
      itemsInColumns[columnIndex].push(items[i])
    }

    return itemsInColumns
  }, [children, columnCount])

  // Memoize column attributes
  const columnAttributes = useMemo<ColumnAttributes>(() => {
    const columnWidth = `${100 / columnCount}%`
    let classNameOutput = columnClassName

    if (classNameOutput && typeof classNameOutput !== "string") {
      logDeprecated('The property "columnClassName" requires a string')
      classNameOutput = "my-masonry-grid_column"
    }

    return {
      ...column,
      ...columnAttrs,
      style: {
        ...columnAttrs?.style,
        width: columnWidth,
      },
      className: classNameOutput,
    }
  }, [column, columnAttrs, columnClassName, columnCount])

  const columnCountCallback = useCallback(() => {
    const columns = reCalculateColumnCount(breakpointCols)
    if (columnCount !== columns) {
      setColumnCount(columns)
    }
  }, [breakpointCols, columnCount])

  const handleWindowResize = useCallback(() => {
    if (!window || !window.requestAnimationFrame) {
      columnCountCallback()
      return
    }

    if (lastFrameRef.current) {
      window.cancelAnimationFrame(lastFrameRef.current)
    }

    lastFrameRef.current = window.requestAnimationFrame(columnCountCallback)
  }, [columnCountCallback])

  useLayoutEffect(() => {
    columnCountCallback()
  }, [columnCountCallback])

  useWindowEvent("resize", handleWindowResize)

  // Memoize className logic
  const classNameOutput = useMemo(() => {
    if (typeof className !== "string") {
      logDeprecated('The property "className" requires a string')
      return "my-masonry-grid"
    }
    return className
  }, [className])

  // Memoize columns rendering
  const renderedColumns = useMemo(() => {
    return childrenInColumns.map((items, i) => (
      <div {...columnAttributes} key={i}>
        {items}
      </div>
    ))
  }, [childrenInColumns, columnAttributes])

  return (
    <div {...rest} className={classNameOutput}>
      {renderedColumns}
    </div>
  )
}

export default React.memo(Masonry)
